import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/contexts/AuthContext";
import ProtectedRoute from "@/components/common/ProtectedRoute";
import TopLoader from "@/components/common/TopLoader";
import PageTransition from "@/components/common/PageTransition";
import Login from "./pages/Login";
import ForgotPassword from "./pages/ForgotPassword";
import FindMember from "./pages/FindMember";

import StudentDashboard from "./pages/student/StudentDashboard";
import Certificates from "./pages/student/Certificates";
import Profile from "./pages/student/Profile";
import GrowLearnDashboard from "./pages/growlearn/GrowLearnDashboard";
import RegisterStudent from "./pages/growlearn/RegisterStudent";
import Students from "./pages/growlearn/Students";
import FailedStudents from "./pages/growlearn/FailedStudents";
import StudentDetail from "./pages/growlearn/StudentDetail";
import EditMemberDetails from "./pages/growlearn/EditMemberDetails";
import GrowLearnManageUsers from "./pages/growlearn/ManageUsers";
import GrowLearnCreateUser from "./pages/growlearn/CreateUser";
import GrowLearnSettings from "./pages/growlearn/Settings";
import ACADashboard from "./pages/aca/ACADashboard";
import ReviewQueue from "./pages/aca/ReviewQueue";
import ReviewApplication from "./pages/aca/ReviewApplication";
import ApprovedList from "./pages/aca/ApprovedList";
import ACASettings from "./pages/aca/Settings";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminManageUsers from "./pages/admin/ManageUsers";
import AdminStudentTable from "./pages/admin/StudentTable";
import NotFound from "./pages/NotFound";

const queryClient = new QueryClient();

// The portal has no landing page of its own; send visitors to the main site.
const HomeRedirect = () => {
  window.location.replace("https://acaglobal.my/");
  return null;
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AuthProvider>
          <TopLoader />
          <PageTransition>
          <Routes>
            <Route path="/" element={<HomeRedirect />} />
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/find-member" element={<FindMember />} />
            

            {/* Student routes */}
            <Route path="/student" element={<ProtectedRoute allowedRoles={["student"]}><StudentDashboard /></ProtectedRoute>} />
            <Route path="/student/certificates" element={<ProtectedRoute allowedRoles={["student"]}><Certificates /></ProtectedRoute>} />
            <Route path="/student/profile" element={<ProtectedRoute allowedRoles={["student"]}><Profile /></ProtectedRoute>} />

            {/* GrowLearn routes */}
            <Route path="/growlearn" element={<ProtectedRoute allowedRoles={["growlearn"]}><GrowLearnDashboard /></ProtectedRoute>} />
            <Route path="/growlearn/register" element={<ProtectedRoute allowedRoles={["growlearn"]}><RegisterStudent /></ProtectedRoute>} />
            <Route path="/growlearn/students" element={<ProtectedRoute allowedRoles={["growlearn"]}><Students /></ProtectedRoute>} />
            <Route path="/growlearn/failed" element={<ProtectedRoute allowedRoles={["growlearn"]}><FailedStudents /></ProtectedRoute>} />
            <Route path="/growlearn/students/:userId" element={<ProtectedRoute allowedRoles={["growlearn"]}><StudentDetail /></ProtectedRoute>} />
            <Route path="/growlearn/students/:userId/edit" element={<ProtectedRoute allowedRoles={["growlearn"]}><EditMemberDetails /></ProtectedRoute>} />
            <Route path="/growlearn/users" element={<ProtectedRoute allowedRoles={["growlearn"]}><GrowLearnManageUsers /></ProtectedRoute>} />
            <Route path="/growlearn/users/new" element={<ProtectedRoute allowedRoles={["growlearn"]}><GrowLearnCreateUser /></ProtectedRoute>} />
            <Route path="/growlearn/settings" element={<ProtectedRoute allowedRoles={["growlearn"]}><GrowLearnSettings /></ProtectedRoute>} />

            {/* ACA routes (no manage users, no admin) */}
            <Route path="/aca" element={<ProtectedRoute allowedRoles={["aca_member"]}><ACADashboard /></ProtectedRoute>} />
            <Route path="/aca/review" element={<ProtectedRoute allowedRoles={["aca_member"]}><ReviewQueue /></ProtectedRoute>} />
            <Route path="/aca/review/:appId" element={<ProtectedRoute allowedRoles={["aca_member"]}><ReviewApplication /></ProtectedRoute>} />
            <Route path="/aca/approved" element={<ProtectedRoute allowedRoles={["aca_member"]}><ApprovedList /></ProtectedRoute>} />
            <Route path="/aca/settings" element={<ProtectedRoute allowedRoles={["aca_member"]}><ACASettings /></ProtectedRoute>} />

            {/* Admin routes (separate from ACA) */}
            <Route path="/admin" element={<ProtectedRoute allowedRoles={["admin"]}><AdminDashboard /></ProtectedRoute>} />
            <Route path="/admin/students" element={<ProtectedRoute allowedRoles={["admin"]}><AdminStudentTable /></ProtectedRoute>} />
            <Route path="/admin/users" element={<ProtectedRoute allowedRoles={["admin"]}><AdminManageUsers /></ProtectedRoute>} />

            <Route path="*" element={<NotFound />} />
          </Routes>
          </PageTransition>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
